import React, { useState } from "react";
import { z } from "zod";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarOff } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { errMsg } from "./calendarUtils";
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";

// A CLOSURE (kind="closed"): one blocked date or a range, with an optional free-text
// reason. Extra opening hours on a date are the other kind of exception and have their
// own dialog (ExtraHoursDialog.jsx).
//
// `date_from` is the only required field (POST /admin/availability-exceptions —
// date_to and reason are both optional there). This used to be enforced only by
// disabling the save button on empty content (house-rule violation: a disabled button
// with no message tells the practitioner nothing they can act on).
const OVERRIDE_SCHEMA = z.object({
  date_from: z.string().min(1, "ovr.dateFromRequired"),
});

export const OverrideDialog = ({ onClose, onSaved }) => {
  const t = useT("admin");
  const tc = useT("common");
  const [form, setForm] = useState({ date_from: "", date_to: "", reason: "" });
  const [saving, setSaving] = useState(false);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const updateField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };

  const save = async () => {
    if (validate(OVERRIDE_SCHEMA, form)) { toast.error(tc("validation.fixHighlighted")); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/availability-exceptions", {
        date_from: form.date_from,
        date_to: form.date_to || undefined,
        reason: form.reason || undefined,
      });
      toast.success(t("ovr.created"));
      // The server never cancels the appointments a closure strands: it reports them, and
      // the practitioner decides whether to move or cancel each one.
      if (data?.conflicts?.length) {
        toast.warning(t("ovr.conflicts", { count: data.conflicts.length }));
      }
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ovr-dialog">
        <DialogHeader>
          <DialogTitle>{t("ovr.dialogTitle")}</DialogTitle>
          <DialogDescription className="sr-only">{t("ovr.dialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{t("ovr.dateFrom")}</Label>
            <Input
              type="date"
              value={form.date_from}
              onChange={(e) => updateField("date_from", e.target.value)}
              className={`mt-1 ${fieldErrors.date_from ? invalidInputClass : ""}`}
              data-testid="ovr-date-from"
            />
            <FieldError message={fieldErrors.date_from ? t(fieldErrors.date_from, null, "Start date required") : ""} testId="ovr-date-from-error" />
          </div>
          <div>
            <Label className="text-xs">{t("ovr.dateTo")}</Label>
            <Input type="date" value={form.date_to} onChange={(e) => updateField("date_to", e.target.value)} className="mt-1" data-testid="ovr-date-to" />
            <p className="text-[11px] text-eh-muted mt-1">{t("ovr.dateToHint")}</p>
          </div>
          <div>
            <Label className="text-xs">{t("ovr.reason")}</Label>
            <Input
              value={form.reason}
              onChange={(e) => updateField("reason", e.target.value)}
              placeholder={t("ovr.reasonPlaceholder")}
              className="mt-1"
              data-testid="ovr-reason"
            />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-eh-ink text-eh-bg hover:opacity-90" data-testid="ovr-save-btn">
            {saving ? "…" : <><CalendarOff className="w-4 h-4 mr-1" /> {t("ovr.create")}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
